
import { ExternalLink, Github, Eye, Images } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";

interface Project {
  id: string;
  title: string;
  description: string;
  image: string;
  tags: string[];
  demoUrl?: string;
  githubUrl?: string;
  videoUrl?: string;
  isSchoolProject: boolean;
  color: 'yellow' | 'blue' | 'green' | 'pink';
  previewImages?: string[];
  displayType?: 'carousel' | 'before_after' | 'video';
  beforeAfter?: {
    before: string;
    after: string;
  };
}

interface ProjectCardProps {
  project: Project;
  onViewDetails: (project: Project) => void;
  onPreview?: (project: Project) => void;
}

const ProjectCard = ({ project, onViewDetails, onPreview }: ProjectCardProps) => {
  const getCardColorClass = (color: string) => {
    const colorMap = {
      'yellow': 'card-yellow-dark',
      'blue': 'card-blue-dark', 
      'green': 'card-green-dark',
      'pink': 'card-pink-dark'
    };
    return colorMap[color as keyof typeof colorMap] || 'card-yellow-dark';
  };

  const getBorderColor = (color: string) => {
    const colorMap = {
      'yellow': 'border-accent',
      'blue': 'border-electric',
      'green': 'border-success', 
      'pink': 'border-pink'
    };
    return colorMap[color as keyof typeof colorMap] || 'border-accent';
  };

  const getAccentColor = (color: string) => {
    const colorMap = {
      'yellow': 'bg-accent/10 text-accent border-accent/20 group-hover:bg-accent-foreground/20 group-hover:text-accent-foreground group-hover:border-accent-foreground/30 transition-all duration-500 ease-in-out',
      'blue': 'bg-electric/10 text-electric border-electric/20 group-hover:bg-electric-foreground/20 group-hover:text-electric-foreground group-hover:border-electric-foreground/30 transition-all duration-500 ease-in-out',
      'green': 'bg-success/10 text-success border-success/20 group-hover:bg-success-foreground/20 group-hover:text-success-foreground group-hover:border-success-foreground/30 transition-all duration-500 ease-in-out',
      'pink': 'bg-pink/10 text-pink border-pink/20 group-hover:bg-pink-foreground/20 group-hover:text-pink-foreground group-hover:border-pink-foreground/30 transition-all duration-500 ease-in-out'
    };
    return colorMap[color as keyof typeof colorMap] || 'bg-accent/10 text-accent border-accent/20 group-hover:bg-accent-foreground/20 group-hover:text-accent-foreground group-hover:border-accent-foreground/30 transition-all duration-500 ease-in-out';
  };

  const getHoverTextColor = (color: string) => {
    const colorMap = {
      'yellow': 'group-hover:text-accent-foreground transition-colors duration-500 ease-in-out',
      'blue': 'group-hover:text-electric-foreground transition-colors duration-500 ease-in-out',
      'green': 'group-hover:text-success-foreground transition-colors duration-500 ease-in-out',
      'pink': 'group-hover:text-pink-foreground transition-colors duration-500 ease-in-out'
    };
    return colorMap[color as keyof typeof colorMap] || 'group-hover:text-accent-foreground transition-colors duration-500 ease-in-out';
  };

  return (
    <Card className={`group hover-lift overflow-hidden shadow-lg rounded-nobilito h-full ${getCardColorClass(project.color)}`}>
      <CardContent className="p-8 relative z-10 h-full flex flex-col">
        <div className="flex items-start justify-between mb-6">
          <Badge 
            variant="outline"
            className={`font-medium ${getAccentColor(project.color)}`}
          >
            {project.isSchoolProject ? "École" : "Professionnel"}
          </Badge>
          
          <div className={`text-6xl text-white ${getHoverTextColor(project.color)}`}>
            {project.isSchoolProject ? "🎓" : "🚀"}
          </div>
        </div>

        <h3 className={`text-2xl font-black mb-4 leading-tight text-white ${getHoverTextColor(project.color)}`}>
          {project.title}
        </h3>
        
        <p className={`mb-6 text-white/70 text-lg leading-relaxed flex-grow ${getHoverTextColor(project.color)}`}>
          {project.description}
        </p>

        <div className="flex flex-wrap gap-2 mb-6">
          {project.tags.slice(0, 3).map((tag) => (
            <span
              key={tag}
              className={`px-3 py-1 rounded-full text-sm font-medium ${getAccentColor(project.color)}`}
            >
              {tag}
            </span>
          ))}
          {project.tags.length > 3 && (
            <span className={`px-3 py-1 rounded-full text-sm font-medium ${getAccentColor(project.color)}`}>
              +{project.tags.length - 3}
            </span>
          )}
        </div>

        <div className="flex flex-wrap gap-3 mt-auto">
          <Button
            size="sm"
            onClick={() => onViewDetails(project)}
            className={`${getAccentColor(project.color)} hover:opacity-80 rounded-full font-medium border`}
            variant="outline"
          >
            <Eye className="h-4 w-4 mr-2" />
            Voir les détails
          </Button>
          
          {((project.displayType === 'carousel' && project.previewImages && project.previewImages.length > 0) ||
            (project.displayType === 'before_after' && project.beforeAfter) ||
            (project.displayType === 'video' && project.videoUrl)) && onPreview && (
            <Button
              size="sm"
              onClick={() => onPreview(project)}
              className={`${getAccentColor(project.color)} hover:opacity-80 rounded-full font-medium border`}
              variant="outline"
            >
              <Images className="h-4 w-4 mr-2" />
              Aperçu
            </Button>
          )}
          
          {project.demoUrl && (
            <Button
              size="sm"
              asChild
              className={`${getAccentColor(project.color)} hover:opacity-80 rounded-full border`}
              variant="outline"
            >
              <a href={project.demoUrl} target="_blank" rel="noopener noreferrer">
                <ExternalLink className="h-4 w-4" />
              </a>
            </Button>
          )}
          
          {project.githubUrl && (
            <Button
              size="sm"
              asChild
              className={`${getAccentColor(project.color)} hover:opacity-80 rounded-full border`}
              variant="outline"
            >
              <a href={project.githubUrl} target="_blank" rel="noopener noreferrer">
                <Github className="h-4 w-4" />
              </a>
            </Button>
          )}
        </div>
      </CardContent>
    </Card>
  );
};

export default ProjectCard;
